import React, { useEffect } from 'react';
import { EventDesignPurpose, EventUserStatus, useEventWebsiteGuestLazyQuery, useGetEventCTAButtonsDataLazyQuery } from '@graphql/generated';
import { useCtaButtons } from '@apps/guest/packages/layout-engine/common/utils/useCtaButtons';
import { useAuth } from '@shared/components/AuthProvider';
import { Skeleton } from '@shared/components/Skeleton';
import { useFeatureValue, useIsFeatureEnabled } from '@shared/core/featureFlags';
import { STANDARD_TRANSITION } from '@shared/utils/animationTransition';
import { useEventInfo } from '@shared/utils/eventInfo';
import { Box, BoxProps, ButtonV2, DrawerV2, JoyKitThemeProvider, PopoverV2, useDisclosure } from '@withjoy/joykit';
import { Close, GuestGuide as GuestGuideIcon } from '@withjoy/joykit/icons';
import { BreakpointAliasKey, pxToRem } from '@withjoy/joykit/theme';
import { useTranslation } from '@shared/core';
import { GuestGuideTelemetryProvider, useGuestGuideTelemetry } from './GuestGuideTelemetry';
import { useCountdown } from '@shared/hooks/useCountdown';
import { useMediaQuery } from '@withjoy/joykit/utils';
import { ReminderModalProvider } from './ReminderModalContext';
import { ReminderModalWrapper } from './ReminderModalWrapper';
import { AddToCalendarAction } from './actions/AddToCalendarAction';
import { EditRSVPAction } from './actions/EditRSVPAction';
import { CarRentalsAction } from './actions/CarRentalsAction';
import { HotelDealsAction } from './actions/HotelDealsAction';
import { BrowseCouplesRegistryAction } from './actions/BrowseCouplesRegistryAction';
import { RemindMeToBookStayAction } from './actions/RemindMeToBookStayAction';

const ALOHA_BREAKPOINT: BreakpointAliasKey = 'sm4';

/**
 * This component relies on EventInfoProvider
 * @returns element for guest guide
 */
export const GuestGuide = () => {
  const { eventInfo, eventHandle, loading: loadingEventInfo } = useEventInfo();
  const { currentUser } = useAuth();
  const [getCtaData, { data: sessionData, called: calledCTA, loading: loadingCTA }] = useGetEventCTAButtonsDataLazyQuery({
    batchMode: 'fast',
    ssr: false,
  });

  const [getEventData, { data: eventData }] = useEventWebsiteGuestLazyQuery({
    batchMode: 'fast',
    ssr: false,
    fetchPolicy: 'cache-only',
  });

  useEffect(() => {
    if (eventHandle) {
      getCtaData({
        variables: { name: eventHandle },
      });
      getEventData({
        variables: {
          eventDesignPurpose: EventDesignPurpose.live,
          eventHandle: eventHandle,
          isAdminDashboard: false,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventHandle]);

  const { getButtonsVisibility } = useCtaButtons();
  const { welcomePage, storyPage, tidbitsPage, schedulePage, travelPage, faqPage, weddingPage, registryPage, momentsPage, rsvpPage, customPages } = sessionData?.eventByName || {};
  const { eventCTA, hasUnlocked } = sessionData?.eventByName?.activeSession || {};
  const { rsvpIsEnabled, scheduleHasInviteOnlyItems } = eventCTA || {};
  const pages = [welcomePage, storyPage, tidbitsPage, schedulePage, travelPage, faqPage, weddingPage, registryPage, momentsPage, rsvpPage, ...(customPages ?? [])];
  const rsvpIsPasswordProtected = rsvpPage?.private;
  const { shouldDisplayUnlockButton } = getButtonsVisibility(pages, welcomePage, schedulePage, rsvpIsEnabled, rsvpIsPasswordProtected, scheduleHasInviteOnlyItems, hasUnlocked);

  // sm4 is the breakpoint for desktop on aloha
  const isDesktop = useMediaQuery(theme => theme.mediaQueries.up({ breakpointAlias: ALOHA_BREAKPOINT }));
  const [showGuestGuide] = useIsFeatureEnabled('showGuestGuide');

  const hasEventPast = (eventInfo?.finalizedDate?.milliseconds || Infinity) < new Date().getMilliseconds();
  const isAdminOrOwner = ([EventUserStatus.admin, EventUserStatus.owner] as Maybe<EventUserStatus>[]).includes(currentUser.activeEventUser?.status);
  const hasRsvpD = sessionData?.eventByName?.activeSession.eventCTA.rsvpIsCompleted;
  const hidden = !showGuestGuide && [!calledCTA, loadingEventInfo, loadingCTA, !eventInfo, isAdminOrOwner, hasEventPast, shouldDisplayUnlockButton, !hasRsvpD].some(Boolean);
  const countDownDateData =
    eventData?.eventByName?.settings?.hideEventCountdown !== true && eventData?.eventByName?.info?.finalizedDate
      ? {
          dateMs: eventData.eventByName.info.finalizedDate.milliseconds,
          timezone: eventData.eventByName.info.finalizedDate.timezone,
        }
      : undefined;
  const countDownDateInThePast = countDownDateData ? countDownDateData.dateMs < Date.now() : false;
  const countDownDate = countDownDateData && !countDownDateInThePast ? countDownDateData : undefined;

  const { isOpen: isGuestGuideOpen, onOpen: onGuestGuideOpen, onClose: onGuestGuideClose } = useDisclosure();

  // Create reusable content for both desktop and mobile wrappers
  const guestGuideContent = <GuestGuideContent countDownDate={countDownDate} />;

  return hidden ? null : (
    // it's easier to measure experiment if the assignment only happens when the Guide should show
    <GuestGuideTelemetryProvider>
      <ReminderModalProvider>
        <GuestGuideFlag>
          <>
            {isDesktop ? (
              <DesktopWrapper
                trigger={<GuestGuideTrigger />}
                isOpen={isGuestGuideOpen}
                onOpen={onGuestGuideOpen}
                onClose={onGuestGuideClose}
              >
                {guestGuideContent}
              </DesktopWrapper>
            ) : (
              <MobileWrapper
                trigger={<GuestGuideTrigger />}
                isOpen={isGuestGuideOpen}
                onOpen={onGuestGuideOpen}
                onClose={onGuestGuideClose}
              >
                {guestGuideContent}
              </MobileWrapper>
            )}
          </>
        </GuestGuideFlag>
        <ReminderModalWrapper />
      </ReminderModalProvider>
    </GuestGuideTelemetryProvider>
  );
};

const GuestGuideFlag = ({ children }: { children: React.ReactNode }) => {
  const { value, loading } = useFeatureValue('guestguideexperiment');
  const hidden = loading || value !== 'treatment';
  return hidden ? null : <>{children}</>;
};

const GuestGuideTrigger = React.forwardRef<HTMLButtonElement>((props, ref) => {
  const { t } = useTranslation('guestGuide');
  return (
    <Box
      ref={ref}
      as="button"
      position={'fixed'}
      // bottom={[pxToRem(16), pxToRem(44)]}
      bottom={{
        _: pxToRem(16),
        [ALOHA_BREAKPOINT]: pxToRem(44),
      }}
      // right={['50%', pxToRem(44)]}
      right={{
        _: '50%',
        [ALOHA_BREAKPOINT]: pxToRem(44),
      }}
      // transform={[`translate(50%, 0)`, 'translate(0, 0)']}
      transform={{
        _: `translate(50%, 0)`,
        [ALOHA_BREAKPOINT]: 'translate(0, 0)',
      }}
      height={pxToRem(56)}
      backgroundColor={'#000000'}
      color={'white'}
      borderRadius={'full'}
      display={'flex'}
      alignItems={'center'}
      gap={3}
      zIndex={100}
      typographyVariant={'button2'}
      paddingX={6}
      transition={`background-color ${STANDARD_TRANSITION}`}
      _hover={{
        backgroundColor: 'mono13',
      }}
      _active={{
        backgroundColor: 'mono14',
      }}
      // need to spread box props for trigger of Popover
      {...props}
    >
      <GuestGuideIcon />
      {t('buttonText')()}
    </Box>
  );
});

const MobileWrapper = ({
  children,
  trigger,
  isOpen,
  onOpen,
  onClose,
}: {
  children: React.ReactNode;
  trigger: React.ReactElement;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
}) => {
  const handleTrigger = () => {
    onOpen();
  };
  const triggerClone = React.cloneElement(trigger, {
    onClick: handleTrigger,
  });
  return (
    <>
      <DrawerV2
        onClose={onClose}
        // disableAutoFocus
        size="auto"
        // overrides={DRAWER_OVERRIDES}
        isOpen={isOpen}
        anchor="bottom"
        useBackdrop
        overrides={{
          // grabbed from admin styles in commonDialog.styles.tsx
          Content: {
            props: {
              height: 'unset',
              borderRadius: '0.5rem 0.5rem 0 0',
              width: '100vw',
              padding: '0',
              maxHeight: '100%',
              overflow: 'auto',
            },
          },
          CloseButton: {
            props: {
              paddingX: pxToRem(16),
              paddingY: pxToRem(16),
              top: 0,
              right: 0,
            },
          },
        }}
      >
        <DrawerV2.CloseButton />
        <DrawerV2.Body padding={0}>{children}</DrawerV2.Body>
      </DrawerV2>
      {triggerClone}
    </>
  );
};

const DesktopWrapper = ({
  children,
  trigger,
  isOpen,
  onOpen,
  onClose,
}: {
  children: React.ReactNode;
  trigger: React.ReactNode;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
}) => {
  return (
    <PopoverV2
      isOpen={isOpen}
      onClose={onClose}
      onOpen={onOpen}
      popperOptions={{
        offset: {
          offset: ({ reference, popper }) => {
            // bottom-right align reference and popper to cover the reference
            // [x, y]
            // -- x is measured from middle of reference (i.e. the trigger)
            // -- y is measured from the top of the reference but there's extra space
            return [(-1 * reference.width) / 2, reference.height * -1 - 8];
          },
        },
      }}
    >
      <PopoverV2.Trigger>{trigger}</PopoverV2.Trigger>
      <PopoverV2.Content
        borderRadius={3}
        overflow={'hidden'}
      >
        {children}
        <ButtonV2
          position={'absolute'}
          intent="neutral"
          shape={'rounded'}
          variant={'ghost'}
          top={0}
          right={0}
          padding={5}
          onClick={onClose}
          cursor={'pointer'}
        >
          <Close size="sm" />
        </ButtonV2>
      </PopoverV2.Content>
    </PopoverV2>
  );
};

const CountDownBannerExperiment = (props: { dateMs: number }) => {
  const { countDown } = useCountdown(props.dateMs);
  const [showBanner] = useIsFeatureEnabled('guestGuideCountDownBannerV2');
  const { t } = useTranslation('guestGuide');
  const message = t('countDownBanner')({
    time: () => (
      <Box
        as="span"
        typographyVariant={'hed4'}
      >
        {t('countDownBannerDays')({ days: countDown.days })}
      </Box>
    ),
  });
  return showBanner ? (
    <Box
      typographyVariant={'body2'}
      marginBottom={4}
    >
      {message}
    </Box>
  ) : null;
};

const GuestGuideContent = ({ countDownDate }: { countDownDate?: { dateMs: number; timezone: string } }) => {
  const { t } = useTranslation('guestGuide');
  const telemetry = useGuestGuideTelemetry();

  useEffect(() => {
    telemetry.guideShown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <JoyKitThemeProvider>
      <Box
        backgroundColor={'white'}
        color={'inherit'}
        padding={6}
        minWidth={{ _: undefined, sm2: pxToRem(404) }}
      >
        {/* Countdown experiment only runs if the countdown should show on the guest site (finalized date and showCountDown != true) */}
        {countDownDate ? <CountDownBannerExperiment dateMs={countDownDate.dateMs} /> : null}
        <Box
          typographyVariant={'hed4'}
          textAlign={['center', 'left']}
        >
          {t('guideTitle')()}
        </Box>
        <GreetingWithEventName marginTop={1} />
        <Box
          marginTop={5}
          display={'grid'}
          rowGap={pxToRem(12)}
        >
          {/* Each action component manages its own visibility and renders its own button */}
          <AddToCalendarAction />
          <EditRSVPAction />
          <BrowseCouplesRegistryAction />
          <HotelDealsAction />
          <CarRentalsAction />
          <RemindMeToBookStayAction />
        </Box>
      </Box>
    </JoyKitThemeProvider>
  );
};
const GreetingWithEventName = (props: BoxProps) => {
  const { eventInfo, loading } = useEventInfo();
  const { t } = useTranslation('guestGuide');
  // todo: loading
  return loading ? (
    <Skeleton />
  ) : (
    <Box
      typographyVariant={'body2'}
      color={'mono12'}
      textAlign={['center', 'left']}
      whiteSpace={'pre-line'}
      {...props}
    >
      {t('guideDescription')({ eventName: eventInfo?.eventDisplayName })}
    </Box>
  );
};
